import React from 'react';
import {Box, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import {NAVY, PageHeader, GuidanceBanner, headCell, BandChip} from '../components/widgets.comp';
import { useDashboard } from '../../../context/DashboardContext';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import { READINESS_BAND_COLORS } from '../../../utils/constants';
import {isSuppressedCell} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

/* Single-hue heat tint (brand blue #3B6FA0), stronger for higher scores */
const tint = (v) => `rgba(59,111,160,${Math.max(0.06, Math.min(0.9, (v || 0) / 100)).toFixed(2)})`;

const getBandColor = (band) => {
    if (!band) return null;
    const lowerBand = band.toLowerCase();
    const matchedKey = Object.keys(READINESS_BAND_COLORS).find(
        (key) => key.toLowerCase() === lowerBand
    );
    return matchedKey ? READINESS_BAND_COLORS[matchedKey] : null;
};

const getCellBg = (band, val) => {
    const color = getBandColor(band);
    if (color && color.startsWith('#') && color.length === 7) {
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, 0.8)`;
    }
    return tint(val);
};

const getHeaderLabel = (c) => {
    if (c?.column_subname !== undefined && c?.column_subname !== null && String(c.column_subname).trim() !== '') {
        const str = String(c.column_subname).trim();
        const numMatch = str.match(/\d+/);
        const number = numMatch ? numMatch[0] : str;
        return `Readiness Area ${number}`;
    }
    return c.column_name;
};

const cell = {textAlign: 'center', py: 1.6, px: 2, borderBottom: '1px solid #F3F4F6', fontSize: '14px', fontWeight: 700, color: NAVY};

const compositeHead = {
    minWidth: 130,
    borderLeft: `2px solid ${NAVY}`, borderRight: `2px solid ${NAVY}`,
    boxShadow: '3px 0 8px -2px rgba(0,0,0,0.35)',
    position: 'relative', zIndex: 1,
};
const colKey = (c, idx) => c.column_name || `col_${idx}`;

/* The heat-map in its own component so the sort/filter hook runs below the tab's loading
   branches. Rows are the grades ({grade, i} — i is the card's row index); the Composite and
   every area column sort as numbers, and filter once there are enough grades (QA 9 Oct 2026).
   A grade under the n floor has no value to sort by. */
const GradeHeatmapTable = ({rows, rubricCols}) => {
    const columns = [
        {key: 'grade', label: 'Grade', type: 'text'},
        ...rubricCols.map((c, idx) => ({
            key: colKey(c, idx), label: getHeaderLabel(c), type: 'number',
            accessor: (r) => (isSuppressedCell(c.cell_data?.[r.i]) ? null : c.values?.[r.i]),
        })),
    ];
    const t = useTableControls(rows, columns);
    return (
        <>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', overflowX: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.04)'}}>
                <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                        <TableRow>
                            <SortFilterHeaderCell {...t.headerProps('grade')} sx={{...headCell, backgroundColor: '#1A445C', textAlign: 'left'}}/>
                            {rubricCols.map((c, idx) => {
                                const isComposite = c.column_name === 'Composite';
                                return (
                                    <SortFilterHeaderCell
                                        key={colKey(c, idx)}
                                        {...t.headerProps(colKey(c, idx))}
                                        sx={{
                                            ...headCell,
                                            textAlign: 'center',
                                            backgroundColor: isComposite ? NAVY : '#1A445C',
                                            ...(isComposite ? compositeHead : {})
                                        }}
                                    />
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length > 0 && t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={1 + rubricCols.length} sx={{...cell, fontWeight: 400, color: '#6A7282', py: 3}}>No grade matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((row) => (
                            <TableRow key={row.grade}>
                                <TableCell sx={{...cell, textAlign: 'left', backgroundColor: 'var(--brand-surface-subtle, #F9FAFB)'}}>{row.grade}</TableCell>
                                {rubricCols.map((col, idx) => {
                                    const isComposite = col.column_name === 'Composite';
                                    const val = col.values?.[row.i];
                                    const cellInfo = col.cell_data?.[row.i];
                                    // A grade under the n floor: a neutral cell with the placeholder — no value, no band colour.
                                    const sup = isSuppressedCell(cellInfo);
                                    const band = sup ? null : (typeof cellInfo === 'string'
                                        ? cellInfo
                                        : (cellInfo?.band || cellInfo?.status || col.bands?.[row.i] || (typeof col.band === 'string' ? col.band : null)));
                                    // The Composite cell is coloured by its band like every area cell (QA 9 Oct, LA-SD-1).
                                    const bg = sup
                                        ? 'var(--brand-surface-subtle, #EEF1F6)'
                                        : (band ? getCellBg(band, val) : tint(val));

                                    return (
                                        <TableCell
                                            key={colKey(col, idx)}
                                            sx={{
                                                ...cell,
                                                backgroundColor: bg,
                                                ...(isComposite ? {
                                                    minWidth: 130,
                                                    borderLeft: `2px solid ${NAVY}`, borderRight: `2px solid ${NAVY}`,
                                                    borderBottom: '1px solid var(--brand-border, #D1D5DB)',
                                                    fontWeight: 800, fontSize: '16px',
                                                    color: NAVY,
                                                    boxShadow: '3px 0 8px -2px rgba(0,0,0,0.2)',
                                                    position: 'relative', zIndex: 1,
                                                } : {})
                                            }}
                                        >
                                            {sup ? <SuppressedValue variant="cell"/> : (val !== undefined && val !== null ? Math.round(val) : '—')}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const GRADE_HEATMAP_SECTION_CODE = 'S_5c';
const CARD_SECTION = GRADE_HEATMAP_SECTION_CODE;

const GradeHeatmapTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTION });

    if (loading) {
        return <CircularLoader minHeight="200px" />;
    }

    // Safely find the section with code "S_5c"
    const s7Section = dashboardData?.cards?.sections?.find(
        (section) => section.section_code === GRADE_HEATMAP_SECTION_CODE
    );

    if (!s7Section) {
        return (
            <NoDataAvailable />
        );
    }

    const { cards } = s7Section;

    // Extract dynamic TitleCard
    const titleCard = cards?.find(c => c.card_type === 'TitleCard');
    const headerTitle = titleCard?.title || "Grade-Level Heatmap";
    const headerSubtitle = titleCard?.footer || "Which grades need targeted support?";

    // Extract table rubric card
    const tableCard = cards?.find(c => c.card_type === 'TableRubricCard');
    const tableObjects = tableCard?.table_objects || [];

    // Find the Grade column (text column) and rubric columns
    const gradeCol = tableObjects.find(c => c.column_type === 'text');
    const rubricCols = tableObjects.filter(c => c.column_type === 'rubric');

    const readinessCols = rubricCols.filter(c => c.column_subname !== undefined && c.column_subname !== null && String(c.column_subname).trim() !== '');

    const grades = gradeCol?.values || [];
    const rows = grades.map((grade, i) => ({grade, i}));

    const bannerText = 'Patterns across grades guide where to focus. Check its coverage before acting.';

    return (
        <Box>
            <PageHeader
                title={headerTitle}
                subtitle={headerSubtitle}
            />

            {tableCard?.title && (
                <Typography sx={{ fontSize: '18px', fontWeight: 800, color: NAVY, mb: 1.5 }}>
                    {tableCard.title}
                </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                {Object.keys(READINESS_BAND_COLORS).map((band) => (
                    <BandChip key={band} label={band} color={READINESS_BAND_COLORS[band]} />
                ))}
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <GradeHeatmapTable rows={rows} rubricCols={rubricCols}/>
                </Grid>
            </Grid>

            {readinessCols.length > 0 && (
                <Box sx={{ mt: 3.5, mb: 1 }}>
                    <Typography sx={{ fontSize: '18px', fontWeight: 800, color: NAVY, mb: 1.5 }}>
                        List of the Readiness Areas
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 2 }}>
                        {readinessCols.map((c, idx) => (
                            <Typography key={c.column_name || idx} sx={{ fontSize: '14px', color: '#1F2937' }}>
                                <strong style={{ fontWeight: 700, color: '#000000' }}>{getHeaderLabel(c)}:</strong> {c.column_name}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            )}

            <GuidanceBanner text={bannerText}/>
        </Box>
    );
};

export default GradeHeatmapTab;
